/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ConfirmationCard
 *
 * @file  ConfirmationCard.jsx
 * @author Satyaki Bose
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import mixins from '../../assets/styles/mixins.scss';
import SafeButtonWrapper from './SafeButtonWrapper';

const StyledPaper = styled('div')({
  '&&': {
    paddingTop: 48,
    paddingRight: 48,
    paddingBottom: 48,
    paddingLeft: 48,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 24,
    width: 584,
    maxWidth: 800,
    borderRadius: 24,
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
    flexShrink: 0,
    boxSizing: 'border-box'
  }
});

const StyledCloseButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    right: '16px',
    top: '16px',
    color: mixins.blackColor,
    fontSize: 18,
    width: 18,
    height: 18
  }
});

const TITLE_TEXT_STYLES = {
  color: mixins.color_text_primary,
  fontSize: mixins.fontSize24,
  fontFamily: mixins.fontFamilyPlusSans,
  lineHeight: '32px',
  fontStyle: 'normal',
  fontWeight: mixins.fontWeight500,
  letterSpacing: '-0.48px',
  margin: 0
};

const StyledTitle = styled(DialogTitle)({
  '&&': {
    padding: 0,
    width: '100%'
  }
});

const StyledTitleText = styled(Typography)({
  '&&': {
    ...TITLE_TEXT_STYLES,
    lineHeight: '30px'
  },
  '&&&.MuiTypography-body1': {
    lineHeight: '30px'
  },
  '&&&.MuiTypography-h2': {
    ...TITLE_TEXT_STYLES,
    lineHeight: '30px'
  }
});

const StyledContent = styled(DialogContent)({
  '&&': {
    padding: 0,
    marginBottom: 0,
    gap: 18,
    display: 'flex',
    flexDirection: 'column'
  }
});

const CONTENT_TEXT_STYLES = {
  color: mixins.color_text_primary,
  fontSize: mixins.fontSize16,
  fontFamily: mixins.fontFamilyPlusSans,
  fontWeight: mixins.fontWeight400,
  lineHeight: '24px',
  margin: 0
};

const StyledContentText = styled(Typography)({
  '&&': {
    ...CONTENT_TEXT_STYLES
  },
  '&&&.MuiTypography-body1': {
    ...CONTENT_TEXT_STYLES
  }
});

const StyledActions = styled(DialogActions)({
  '&&': {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    margin: 0
  }
});

const CANCEL_BUTTON_TEXT_STYLES = {
  color: mixins.navyColor,
  fontSize: mixins.fontSize16,
  fontWeight: mixins.fontWeight500,
  fontFamily: mixins.fontFamilyPlusSans,
  textTransform: 'none',
  lineHeight: '16px',
  alignItems: 'center',
  display: 'inline-flex',
  minWidth: 104,
  justifyContent: 'center'
};

const CONFIRM_BUTTON_TEXT_STYLES = {
  color: mixins.whiteFont,
  fontSize: mixins.fontSize16,
  fontWeight: mixins.fontWeight500,
  fontFamily: mixins.fontFamilyPlusSans,
  textTransform: 'none',
  lineHeight: '16px'
};

const CANCEL_BUTTON_STYLES = {
  borderRadius: '1000px',
  padding: '12px 20px',
  minWidth: 'unset',
  minHeight: 24,
  textTransform: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  color: mixins.navyColor,
  border: `2px solid ${mixins.navyColor}`,
  '&:hover': {
    backgroundColor: 'transparent',
    color: mixins.navyColor,
    border: `2px solid ${mixins.navyColor}`,
    boxShadow: 'none'
  }
};

const CONFIRM_BUTTON_STYLES = {
  borderRadius: '1000px',
  padding: '12px 20px',
  minWidth: 'unset',
  minHeight: 24,
  textTransform: 'none',
  boxShadow: 'none',
  backgroundColor: mixins.amethyst,
  color: mixins.whiteFont,
  '&:hover': {
    backgroundColor: mixins.amethyst,
    boxShadow: 'none'
  }
};

const StyledCancelButton = styled(Button)({
  '&&': {
    ...CANCEL_BUTTON_STYLES
  },
  '&&&.MuiButton-outlined': {
    backgroundColor: 'transparent',
    borderColor: mixins.navyColor,
    borderWidth: 2,
    color: mixins.navyColor
  },
  '&&&.MuiButton-outlined:hover': {
    backgroundColor: 'transparent',
    borderColor: mixins.navyColor,
    borderWidth: 2
  }
});

const StyledCancelButtonText = styled(Typography)({
  '&&': {
    ...CANCEL_BUTTON_TEXT_STYLES
  },
  '&&&.MuiTypography-body1': {
    ...CANCEL_BUTTON_TEXT_STYLES
  }
});

const StyledConfirmButton = styled(Button)({
  '&&': {
    ...CONFIRM_BUTTON_STYLES
  },
  '&&&.MuiButton-contained': {
    backgroundColor: mixins.amethyst,
    color: mixins.whiteFont
  },
  '&&&.MuiButton-contained:hover': {
    backgroundColor: mixins.amethyst
  }
});

const StyledConfirmButtonText = styled(Typography)({
  '&&': {
    ...CONFIRM_BUTTON_TEXT_STYLES
  },
  '&&&.MuiTypography-body1': {
    ...CONFIRM_BUTTON_TEXT_STYLES
  }
});

const FULL_OVERRIDE_KEYS = new Set([
  'titleText',
  'contentText',
  'cancelButton',
  'cancelButtonText',
  'confirmButton',
  'confirmButtonText'
]);

const isOverrideComponent = (value) => (
  typeof value === 'function'
  || (typeof value === 'object' && value !== null && typeof value.render === 'function')
);

const ConfirmationCard = ({
  open,
  onClose,
  title,
  content,
  cancelText,
  confirmText,
  onCancel,
  onConfirm,
  showCloseButton,
  className,
  titleClassName,
  contentClassName,
  actionsClassName,
  classes,
  isLoading
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    if (onClose) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (onClose) {
      onClose();
    }
  };

  const renderStyledButtonText = (textKey, text, textStyles, DefaultStyledText) => {
    const textClass = classes[textKey];

    if (textClass && FULL_OVERRIDE_KEYS.has(textKey) && isOverrideComponent(textClass)) {
      const TextComponent = textClass;

      return (
        <TextComponent component="span">
          {text}
        </TextComponent>
      );
    }

    if (textClass && FULL_OVERRIDE_KEYS.has(textKey)) {
      return (
        <Typography className={textClass} component="span" sx={textStyles}>
          {text}
        </Typography>
      );
    }

    return (
      <DefaultStyledText className={textClass || ''} component="span">
        {text}
      </DefaultStyledText>
    );
  };

  const renderContentText = (text, index) => {
    if (classes.contentText && FULL_OVERRIDE_KEYS.has('contentText') && isOverrideComponent(classes.contentText)) {
      const ContentTextComponent = classes.contentText;

      return (
        <ContentTextComponent key={index} component="p" sx={CONTENT_TEXT_STYLES}>
          {text}
        </ContentTextComponent>
      );
    }

    if (classes.contentText && FULL_OVERRIDE_KEYS.has('contentText')) {
      return (
        <Typography key={index} className={classes.contentText} component="p" sx={CONTENT_TEXT_STYLES}>
          {text}
        </Typography>
      );
    }

    return (
      <StyledContentText key={index} className={classes.contentText || ''} component="p">
        {text}
      </StyledContentText>
    );
  };

  const renderContent = () => {
    if (typeof content === 'string') {
      return renderContentText(content, 0);
    }
    if (Array.isArray(content)) {
      return content.map((text, index) => renderContentText(text, index));
    }

    return content;
  };

  const renderTitleText = () => {
    if (classes.titleText && FULL_OVERRIDE_KEYS.has('titleText') && isOverrideComponent(classes.titleText)) {
      const TitleTextComponent = classes.titleText;

      return (
        <TitleTextComponent component="h2" sx={TITLE_TEXT_STYLES}>
          {title}
        </TitleTextComponent>
      );
    }

    if (classes.titleText && FULL_OVERRIDE_KEYS.has('titleText')) {
      return (
        <Typography className={classes.titleText} component="h2" sx={TITLE_TEXT_STYLES}>
          {title}
        </Typography>
      );
    }

    return (
      <StyledTitleText className={classes.titleText || ''} component="h2">
        {title}
      </StyledTitleText>
    );
  };

  const renderCancelButton = () => {
    const hasCustomCancelButton = (
      classes.cancelButton
      && FULL_OVERRIDE_KEYS.has('cancelButton')
      && typeof classes.cancelButton !== 'string'
    );

    if (hasCustomCancelButton) {
      const CancelButtonComponent = classes.cancelButton;
      const CancelButtonTextComponent = (
        classes.cancelButtonText
        && FULL_OVERRIDE_KEYS.has('cancelButtonText')
        && typeof classes.cancelButtonText !== 'string'
      ) ? classes.cancelButtonText : StyledCancelButtonText;

      return (
        <CancelButtonComponent
          onClick={handleCancel}
          variant="outlined"
          disableElevation
        >
          {classes.cancelButtonText && FULL_OVERRIDE_KEYS.has('cancelButtonText') ? (
            <CancelButtonTextComponent component="span">
              {cancelText}
            </CancelButtonTextComponent>
          ) : (
            <StyledCancelButtonText className={classes.cancelButtonText || ''} component="span">
              {cancelText}
            </StyledCancelButtonText>
          )}
        </CancelButtonComponent>
      );
    }

    if (classes.cancelButton && FULL_OVERRIDE_KEYS.has('cancelButton')) {
      return (
        <Button
          className={classes.cancelButton}
          onClick={handleCancel}
          variant="outlined"
          disableElevation
          sx={CANCEL_BUTTON_STYLES}
        >
          {classes.cancelButtonText && FULL_OVERRIDE_KEYS.has('cancelButtonText') ? (
            <Typography className={classes.cancelButtonText} component="span" sx={CANCEL_BUTTON_TEXT_STYLES}>
              {cancelText}
            </Typography>
          ) : (
            <StyledCancelButtonText className={classes.cancelButtonText || ''} component="span">
              {cancelText}
            </StyledCancelButtonText>
          )}
        </Button>
      );
    }

    return (
      <StyledCancelButton
        className={classes.cancelButton || ''}
        onClick={handleCancel}
        variant="outlined"
        disableElevation
      >
        {renderStyledButtonText(
          'cancelButtonText',
          cancelText,
          CANCEL_BUTTON_TEXT_STYLES,
          StyledCancelButtonText
        )}
      </StyledCancelButton>
    );
  };

  const renderConfirmButton = () => {
    const hasCustomConfirmButton = (
      classes.confirmButton
      && FULL_OVERRIDE_KEYS.has('confirmButton')
      && typeof classes.confirmButton !== 'string'
    );

    if (hasCustomConfirmButton) {
      const ConfirmButtonComponent = classes.confirmButton;
      const ConfirmButtonTextComponent = (
        classes.confirmButtonText
        && FULL_OVERRIDE_KEYS.has('confirmButtonText')
        && typeof classes.confirmButtonText !== 'string'
      ) ? classes.confirmButtonText : StyledConfirmButtonText;

      return (
        <ConfirmButtonComponent
          onClick={handleConfirm}
          variant="contained"
          disableElevation
        >
          {classes.confirmButtonText && FULL_OVERRIDE_KEYS.has('confirmButtonText') ? (
            <ConfirmButtonTextComponent component="span">
              {confirmText}
            </ConfirmButtonTextComponent>
          ) : (
            <StyledConfirmButtonText className={classes.confirmButtonText || ''} component="span">
              {confirmText}
            </StyledConfirmButtonText>
          )}
        </ConfirmButtonComponent>
      );
    }

    if (classes.confirmButton && FULL_OVERRIDE_KEYS.has('confirmButton')) {
      return (
        <Button
          className={classes.confirmButton}
          onClick={handleConfirm}
          variant="contained"
          disableElevation
          sx={CONFIRM_BUTTON_STYLES}
        >
          {classes.confirmButtonText && FULL_OVERRIDE_KEYS.has('confirmButtonText') ? (
            <Typography className={classes.confirmButtonText} component="span" sx={CONFIRM_BUTTON_TEXT_STYLES}>
              {confirmText}
            </Typography>
          ) : (
            <StyledConfirmButtonText className={classes.confirmButtonText || ''} component="span">
              {confirmText}
            </StyledConfirmButtonText>
          )}
        </Button>
      );
    }

    return (
      <StyledConfirmButton
        className={classes.confirmButton || ''}
        onClick={handleConfirm}
        variant="contained"
        disableElevation
      >
        {renderStyledButtonText(
          'confirmButtonText',
          confirmText,
          CONFIRM_BUTTON_TEXT_STYLES,
          StyledConfirmButtonText
        )}
      </StyledConfirmButton>
    );
  };

  const ActionsComponent = isOverrideComponent(classes?.actions) ? classes.actions : StyledActions;
  const mergedActionsClassName = isOverrideComponent(classes?.actions)
    ? (actionsClassName || '')
    : `${classes.actions || ''} ${actionsClassName || ''}`.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className={className}
      slotProps={{
        paper: {
          component: StyledPaper,
          className: classes.paper || ''
        }
      }}
    >
      {showCloseButton && onClose && (
        <StyledCloseButton
          className={classes.closeButton || ''}
          onClick={onClose}
        >
          <CloseIcon />
        </StyledCloseButton>
      )}
      <StyledTitle className={`${classes.title || ''} ${titleClassName || ''}`.trim()}>
        {renderTitleText()}
      </StyledTitle>
      <StyledContent className={`${classes.content || ''} ${contentClassName || ''}`.trim()}>
        {renderContent()}
      </StyledContent>
      <ActionsComponent className={mergedActionsClassName}>
        <SafeButtonWrapper isLoading={isLoading}>
          {renderCancelButton()}
        </SafeButtonWrapper>
        <SafeButtonWrapper isLoading={isLoading}>
          {renderConfirmButton()}
        </SafeButtonWrapper>
      </ActionsComponent>
    </Dialog>
  );
};

ConfirmationCard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.node
  ]).isRequired,
  cancelText: PropTypes.string,
  confirmText: PropTypes.string,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  actionsClassName: PropTypes.string,
  classes: PropTypes.object,
  isLoading: PropTypes.bool
};

ConfirmationCard.defaultProps = {
  onClose: null,
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  onCancel: null,
  onConfirm: null,
  showCloseButton: true,
  className: '',
  titleClassName: '',
  contentClassName: '',
  actionsClassName: '',
  classes: {},
  isLoading: false
};

const mergeStyleClasses = (defaultClasses, overrideClasses = {}) => {
  const merged = { ...defaultClasses };

  Object.keys(overrideClasses).forEach((key) => {
    const overrideClass = overrideClasses[key];

    if (overrideClass) {
      if (FULL_OVERRIDE_KEYS.has(key)) {
        merged[key] = overrideClass;
      } else {
        merged[key] = defaultClasses[key]
          ? `${defaultClasses[key]} ${overrideClass}`
          : overrideClass;
      }
    }
  });

  return merged;
};

const ConfirmationCardWithStyles = (props) => {
  const { classes: overrideClasses = {}, ...rest } = props;
  const classes = mergeStyleClasses({}, overrideClasses);

  return <ConfirmationCard {...rest} classes={classes} />;
};

ConfirmationCardWithStyles.propTypes = {
  classes: PropTypes.object
};

ConfirmationCardWithStyles.defaultProps = {
  classes: {}
};

export default ConfirmationCardWithStyles;
